import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Select, SelectTrigger } from "@activecollab/components";
import PropTypes from "prop-types";
import { getAllCurrenciesSortedByCurrencyCode, getDefaultCurrency } from "~/store/selectors/selector.currencies";

const SelectCurrency = ({
  target,
  includeSystemDefault = false,
  options,
  selected,
  onChange,
  placeholder,
  noResultText,
  targetClassName,
  ...rest
}) => {
  const { t } = useTranslation();
  const currencies = useSelector(getAllCurrenciesSortedByCurrencyCode);
  const defaultCurrency = useSelector(getDefaultCurrency);

  const defaultCurrencyLabel = useMemo(() => {
    return t(`System Default (${defaultCurrency.code})`);
  }, [t, defaultCurrency]);

  const currenciesWithCode = useMemo(() => {
    let result = [];

    if (options && options.length > 0) {
      result = options.map((c) => {
        return { ...c, name: `${c.code} - ${c.name}` };
      });
    } else {
      result = currencies.map((c) => {
        return { ...c, name: `${c.code} - ${c.name}` };
      });
    }

    if (includeSystemDefault) {
      result.unshift({
        id: 0,
        name: defaultCurrencyLabel,
      });
    }

    return result;
  }, [currencies, options, includeSystemDefault, defaultCurrencyLabel]);

  const currencyName = useMemo(() => {
    if (includeSystemDefault && selected === 0) {
      return defaultCurrencyLabel;
    }

    if (selected > 0 && currenciesWithCode.length) {
      const currency = currenciesWithCode.find((currency) => currency.id === selected);
      if (currency && currency.code && currency.name) {
        return currency.name;
      }
    }
    return undefined;
  }, [currenciesWithCode, selected, includeSystemDefault, defaultCurrencyLabel]);

  const trigger = useMemo(() => {
    if (target) return target;

    return <SelectTrigger className={targetClassName}>{currencyName ?? placeholder}</SelectTrigger>;
  }, [currencyName, placeholder, target, targetClassName]);

  return (
    <Select
      target={trigger}
      options={currenciesWithCode}
      selected={selected}
      onChange={onChange}
      placeholder={placeholder ? placeholder : t("Choose a currency")}
      noResultText={noResultText ? noResultText : t("No result")}
      disabledInternalSort
      keepSameOptionsOrder
      forceCloseMenu
      {...rest}
    />
  );
};

SelectCurrency.propTypes = {
  target: PropTypes.element,
  includeSystemDefault: PropTypes.bool,
  selected: PropTypes.number,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  placeholder: PropTypes.string,
  noResultText: PropTypes.string,
  targetClassName: PropTypes.string,
};

SelectCurrency.defaultProps = {
  placeholder: "",
};

export default SelectCurrency;
